/**
 * NAD Stimulants Section Component
 * Handles animations and interactions for the stimulants cards section
 */

export class NADStimulantsSection {
  private element: HTMLElement;
  private cards: NodeListOf<HTMLElement>;
  private observer: IntersectionObserver | null = null;
  private resizeTimeout: NodeJS.Timeout | null = null;

  constructor(element: HTMLElement) {
    this.element = element;
    this.cards = this.element.querySelectorAll('.nad-stimulants-section__card');
    this.init();
  }

  private init(): void {
    console.log('NAD Stimulants Section initialized');
    this.setupIntersectionObserver();
    this.bindEvents();
  }

  private setupIntersectionObserver(): void {
    // Only setup observer if user prefers motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const options: IntersectionObserverInit = {
      threshold: 0.15,
      rootMargin: '0px 0px -100px 0px'
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const card = entry.target as HTMLElement;
          card.classList.add('nad-stimulants-section__card--visible');
          
          // Unobserve after animation triggers
          this.observer?.unobserve(card);
        }
      });
    }, options);

    // Observe all cards
    this.cards.forEach((card) => {
      this.observer?.observe(card);
    });
  }

  private bindEvents(): void {
    // Handle window resize for responsive adjustments
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  private handleResize(): void {
    // Debounce resize handler
    if (this.resizeTimeout !== null) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      this.adjustLayoutForViewport();
    }, 250);
  }

  private adjustLayoutForViewport(): void {
    // Add any viewport-specific adjustments here
    const isMobile = window.innerWidth < 768;
    const isTablet = window.innerWidth < 1024;
    
    if (isMobile) {
      this.element.classList.add('nad-stimulants-section--mobile');
      this.element.classList.remove('nad-stimulants-section--tablet');
    } else if (isTablet) {
      this.element.classList.add('nad-stimulants-section--tablet');
      this.element.classList.remove('nad-stimulants-section--mobile');
    } else {
      this.element.classList.remove('nad-stimulants-section--mobile', 'nad-stimulants-section--tablet');
    }
  }

  public destroy(): void {
    // Clean up observers
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Remove event listeners
    window.removeEventListener('resize', this.handleResize.bind(this));

    // Clear timeout
    if (this.resizeTimeout !== null) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }

    console.log('NAD Stimulants Section destroyed');
  }

  // Static method for automatic initialization
  static initializeAll(): void {
    const elements = document.querySelectorAll<HTMLElement>('.nad-stimulants-section');
    elements.forEach((element) => {
      if (!(element as any)._nadStimulantsSectionInstance) {
        (element as any)._nadStimulantsSectionInstance = new NADStimulantsSection(element);
      }
    });
  }

  // Static method for cleanup
  static destroyAll(): void {
    const elements = document.querySelectorAll<HTMLElement>('.nad-stimulants-section');
    elements.forEach((element) => {
      const instance = (element as any)._nadStimulantsSectionInstance;
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
        delete (element as any)._nadStimulantsSectionInstance;
      }
    });
  }
}
